/**
 * LevNutri - Relatório de Evolução (Impressão)
 * - Gráficos SVG puros (sem Recharts) → funcionam em @media print
 * - Silhuetas por sexo: PNG /silhueta-homem.png / /silhueta-mulher.png
 * - React Portal para evitar repetição de header/footer
 * - Modo 1 (1 medição): Avaliação Inicial + tabela OMRON + retorno
 * - Modo 2 (2+ medições): Evolução comparativa + gráficos + retorno
 */

import React from 'react';
import ReactDOM from 'react-dom';
import { Ico, emo } from '../ui/Ico';
import { useMarca, LOGO_LEVNUTRI, reportLogoStyle } from '../../branding';

const BRAND     = '#D4A574';
const FONT      = 'Nunito, sans-serif';
const SERIF     = 'Nunito, sans-serif';

// ─────────────────────────────────────────────────────────────────────────────
// CSS  (injected once via PrintEvolutionCSS)
// ─────────────────────────────────────────────────────────────────────────────

export function PrintEvolutionCSS() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@300;400;500;600;700;800&display=swap');

      /* Portal oculto na tela, visível na impressão */
      @media screen {
        .lev-print-evolution { display: none; }
        .screen-only { display: block; }
        .print-only  { display: none !important; }
      }
      @media print {
        body > *:not(.lev-print-evolution) { display: none !important; }
        body > .lev-print-evolution        { display: block !important; }
        .screen-only { display: none !important; }
        .print-only  { display: block !important; }
        @page { size: A4 portrait; margin: 1.4cm 1.8cm; }
        .no-break { page-break-inside: avoid; break-inside: avoid; }
        img { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .silhueta-img { height: 260px !important; width: auto !important; }
      }
    `}</style>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PrintLineChart  –  SVG puro, sem nenhuma biblioteca externa
// ─────────────────────────────────────────────────────────────────────────────

export function PrintLineChart({ data, dataKey, color, unit, title, width = 250, height = 120 }) {
  const PAD = { l: 32, r: 6, t: 16, b: 20 };
  const W = width  - PAD.l - PAD.r;
  const H = height - PAD.t - PAD.b;

  const pts = data
    .map((d, i) => ({ d, i, v: parseFloat(d[dataKey]) }))
    .filter(p => !isNaN(p.v) && p.v != null);

  if (pts.length < 1) return null;

  const vals  = pts.map(p => p.v);
  const minV  = Math.min(...vals);
  const maxV  = Math.max(...vals);
  const range = maxV === minV ? (maxV === 0 ? 1 : Math.abs(maxV) * 0.25) : maxV - minV;
  const pad_v = range * 0.18;

  const sx = (i) => PAD.l + (data.length < 2 ? W / 2 : (i / (data.length - 1)) * W);
  const sy = (v) => PAD.t + (1 - (v - (minV - pad_v)) / (range + pad_v * 2)) * H;

  const linePath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${sx(p.i).toFixed(1)},${sy(p.v).toFixed(1)}`).join(' ');
  const areaPath = pts.length >= 2
    ? `${linePath} L${sx(pts.at(-1).i).toFixed(1)},${(PAD.t + H).toFixed(1)} L${sx(pts[0].i).toFixed(1)},${(PAD.t + H).toFixed(1)} Z`
    : '';

  const yTicks = [minV, minV + range / 2, maxV].map(v => ({
    v, y: sy(v),
    lbl: Math.abs(v) >= 1000 ? Math.round(v).toString()
       : Number.isInteger(v) ? v.toString()
       : v.toFixed(1),
  }));

  const step = Math.max(1, Math.ceil(data.length / 5));
  const xLabels = [];
  for (let i = 0; i < data.length; i += step) xLabels.push(i);
  if (xLabels.at(-1) !== data.length - 1) xLabels.push(data.length - 1);

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}
      style={{ display: 'block', overflow: 'visible' }}>
      <text x={PAD.l + W / 2} y={10} textAnchor="middle"
        fontSize="7.5" fill="#736b64" fontFamily={FONT} fontWeight="600">{emo(title)}</text>
      {yTicks.map((t, i) => (
        <g key={i}>
          <line x1={PAD.l} y1={t.y} x2={PAD.l + W} y2={t.y}
            stroke={i === 0 ? '#d9d0c6' : '#ebe3da'} strokeWidth="0.8"/>
          <text x={PAD.l - 2} y={t.y + 3} textAnchor="end"
            fontSize="6" fill="#a39b93" fontFamily={FONT}>{t.lbl}</text>
        </g>
      ))}
      <line x1={PAD.l} y1={PAD.t} x2={PAD.l} y2={PAD.t + H} stroke="#d9d0c6" strokeWidth="0.8"/>
      <line x1={PAD.l} y1={PAD.t + H} x2={PAD.l + W} y2={PAD.t + H} stroke="#d9d0c6" strokeWidth="0.8"/>
      {areaPath && <path d={areaPath} fill={color} fillOpacity="0.13"/>}
      {pts.length >= 2 && (
        <path d={linePath} fill="none" stroke={color} strokeWidth="1.7"
          strokeLinecap="round" strokeLinejoin="round"/>
      )}
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={sx(p.i)} cy={sy(p.v)} r="2.8" fill={color} stroke="#ffffff" strokeWidth="1"/>
          {pts.length <= 4 && (
            <text x={sx(p.i)} y={sy(p.v) - 5} textAnchor="middle"
              fontSize="6.5" fill={color} fontFamily={FONT} fontWeight="700">
              {p.v % 1 === 0 ? p.v : p.v.toFixed(1)}{unit}
            </text>
          )}
        </g>
      ))}
      {xLabels.map((idx, i) => (
        <text key={i} x={sx(idx)} y={height - 3}
          textAnchor={i === 0 ? 'start' : i === xLabels.length - 1 ? 'end' : 'middle'}
          fontSize="6.5" fill="#a39b93" fontFamily={FONT}>
          {data[idx]?.data_label || ''}
        </text>
      ))}
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BodyWithAnnotations  –  silhueta + labels laterais (exportado para index.jsx)
// ─────────────────────────────────────────────────────────────────────────────

export function BodyWithAnnotations({ gender, last, first, printMode = false }) {
  const goodDown = (k) => ['peso','gordura_corporal','gordura_visceral','imc','idade_corporal'].includes(k);

  const diffColor = (key) => {
    if (!first || !last || first.id === last.id) return '#736b64';
    const d = parseFloat(last[key]) - parseFloat(first[key]);
    if (isNaN(d)) return '#736b64';
    return goodDown(key) ? (d < 0 ? '#46714f' : d > 0 ? '#a8483f' : '#736b64')
                         : (d > 0 ? '#46714f' : d < 0 ? '#a8483f' : '#736b64');
  };

  const diffTxt = (key) => {
    if (!first || !last || first.id === last.id) return null;
    const d = parseFloat(last[key]) - parseFloat(first[key]);
    if (isNaN(d)) return null;
    return (d > 0 ? '+' : '') + d.toFixed(1);
  };

  const fmt = (v, u = '') => (v != null && v !== '' ? `${v}${u}` : '-');

  const labelStyle = (align = 'right') => ({
    textAlign: align,
    fontFamily: FONT,
    minWidth: printMode ? '62pt' : '72px',
  });

  const nameStyle = { margin: 0, fontSize: printMode ? '7pt' : '0.72rem', color: '#736b64' };
  const valStyle  = { margin: '1px 0 0', fontSize: printMode ? '10.5pt' : '1.05rem', fontWeight: 600, color: '#2f2a26', fontFamily: FONT };
  const diffStyle = (key) => ({ margin: 0, fontSize: printMode ? '7.5pt' : '0.78rem', fontWeight: 600, color: diffColor(key), fontFamily: FONT });

  const MetricLabel = ({ mkey, label, unit, align }) => (
    <div style={labelStyle(align)}>
      <p style={nameStyle}>{emo(label)}</p>
      <p style={valStyle}>{fmt(last?.[mkey], unit)}</p>
      {diffTxt(mkey) && <p style={diffStyle(mkey)}>{diffTxt(mkey)}{unit}</p>}
    </div>
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: printMode ? '10pt' : '12px', justifyContent: 'center' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: printMode ? '10pt' : '14px', alignItems: 'flex-end' }}>
        <MetricLabel mkey="gordura_corporal"      label="% Gordura"    unit="%"      align="right"/>
        <MetricLabel mkey="gordura_visceral"       label="Visceral"     unit=" nív."  align="right"/>
        <MetricLabel mkey="metabolismo_basal"      label="Metab. Basal" unit=" kcal"  align="right"/>
      </div>
      <div style={{ flexShrink: 0 }}>
        <img
          className="silhueta-img"
          src={gender === 'M' ? '/silhueta-homem.png' : '/silhueta-mulher.png'}
          style={{ height: printMode ? '260px' : '300px', width: 'auto', display: 'block' }}
          alt=""
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: printMode ? '10pt' : '14px', alignItems: 'flex-start' }}>
        <MetricLabel mkey="musculos_esqueleticos"  label="% Músculos"  unit="%"      align="left"/>
        <MetricLabel mkey="imc"                    label="IMC"         unit=""       align="left"/>
        <MetricLabel mkey="idade_corporal"          label="Idade Corp." unit=" anos"  align="left"/>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Funções de classificação OMRON HBF-514C
// ─────────────────────────────────────────────────────────────────────────────

export function classifyIMC(imc) {
  const v = parseFloat(imc);
  if (isNaN(v)) return null;
  if (v < 18.5) return { label: 'Abaixo do peso',  color: '#7189a6' };
  if (v < 25.0) return { label: 'Peso normal',      color: '#46714f' };
  if (v < 30.0) return { label: 'Sobrepeso',        color: '#c4933f' };
  if (v < 35.0) return { label: 'Obesidade grau I', color: '#a8483f' };
  return                { label: 'Obesidade grau II+', color: '#a8483f' };
}

export function classifyGordura(gc, gender) {
  const v = parseFloat(gc);
  if (isNaN(v)) return null;
  const isMale = gender === 'M';
  if (isMale) {
    if (v < 8)   return { label: 'Muito baixo', color: '#7189a6' };
    if (v < 20)  return { label: 'Normal',       color: '#46714f' };
    if (v < 25)  return { label: 'Moderado',     color: '#c4933f' };
    return              { label: 'Alto',          color: '#a8483f' };
  } else {
    if (v < 13)  return { label: 'Muito baixo', color: '#7189a6' };
    if (v < 26)  return { label: 'Normal',       color: '#46714f' };
    if (v < 33)  return { label: 'Moderado',     color: '#c4933f' };
    return              { label: 'Alto',          color: '#a8483f' };
  }
}

export function classifyVisceral(gv) {
  const v = parseFloat(gv);
  if (isNaN(v)) return null;
  if (v <= 9)  return { label: 'Normal',     color: '#46714f' };
  if (v <= 14) return { label: 'Alto',       color: '#c4933f' };
  return              { label: 'Muito alto', color: '#a8483f' };
}

export function classifyMuscle(ms, gender) {
  const v = parseFloat(ms);
  if (isNaN(v)) return null;
  const isMale = gender === 'M';
  if (isMale) {
    if (v < 33)  return { label: 'Baixo',      color: '#c4933f' };
    if (v < 36)  return { label: 'Normal',      color: '#46714f' };
    if (v < 40)  return { label: 'Alto',        color: '#46714f' };
    return              { label: 'Muito alto',  color: '#46714f' };
  } else {
    if (v < 24)  return { label: 'Baixo',      color: '#c4933f' };
    if (v < 28)  return { label: 'Normal',      color: '#46714f' };
    if (v < 33)  return { label: 'Alto',        color: '#46714f' };
    return              { label: 'Muito alto',  color: '#46714f' };
  }
}

export function classifyCorpAge(idadeCorp, idadeReal) {
  const ic = parseFloat(idadeCorp);
  const ir = parseFloat(idadeReal);
  if (isNaN(ic) || isNaN(ir)) return null;
  const diff = ic - ir;
  if (diff <= -3) return { label: `${Math.abs(diff)} anos abaixo`, color: '#46714f' };
  if (diff <= 2)  return { label: 'Equivalente à idade real',       color: '#46714f' };
  if (diff <= 5)  return { label: `${diff} anos acima`,             color: '#c4933f' };
  return                 { label: `${diff} anos acima`,             color: '#a8483f' };
}

// ─────────────────────────────────────────────────────────────────────────────
// ClassificationTable – tabela OMRON para a 1ª avaliação
// ─────────────────────────────────────────────────────────────────────────────

function ClassificationTable({ record, gender, idadeReal }) {
  if (!record) return null;
  const rows = [
    {
      label: 'IMC',
      value: record.imc ? `${parseFloat(record.imc).toFixed(1)}` : '-',
      cls: classifyIMC(record.imc),
      ref: 'Saudável: 18,5 – 24,9',
    },
    {
      label: 'Gordura Corporal',
      value: record.gordura_corporal ? `${parseFloat(record.gordura_corporal).toFixed(1)}%` : '-',
      cls: classifyGordura(record.gordura_corporal, gender),
      ref: gender === 'M' ? 'Normal H: 8 – 20%' : 'Normal M: 13 – 26%',
    },
    {
      label: 'Gordura Visceral',
      value: record.gordura_visceral ? `nível ${record.gordura_visceral}` : '-',
      cls: classifyVisceral(record.gordura_visceral),
      ref: 'Normal: níveis 1 – 9',
    },
    {
      label: 'Músculos Esqueléticos',
      value: record.musculos_esqueleticos ? `${parseFloat(record.musculos_esqueleticos).toFixed(1)}%` : '-',
      cls: classifyMuscle(record.musculos_esqueleticos, gender),
      ref: gender === 'M' ? 'Normal H: 33 – 36%' : 'Normal M: 24 – 28%',
    },
    {
      label: 'Idade Corporal',
      value: record.idade_corporal ? `${record.idade_corporal} anos` : '-',
      cls: classifyCorpAge(record.idade_corporal, idadeReal),
      ref: idadeReal ? `Idade real: ${idadeReal} anos` : '',
    },
  ];

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8pt', fontFamily: FONT }}>
      <thead>
        <tr style={{ background: '#fbf8f5' }}>
          {['Indicador', 'Valor', 'Classificação', 'Referência'].map(h => (
            <th key={h} style={{ padding: '4pt 5pt', textAlign: h === 'Indicador' ? 'left' : 'center',
              color: '#4a4440', fontWeight: 600 }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(({ label, value, cls, ref }) => (
          <tr key={label} style={{ borderBottom: '0.5pt solid #ebe3da' }}>
            <td style={{ padding: '3pt 5pt', fontWeight: 500 }}>{emo(label)}</td>
            <td style={{ padding: '3pt 5pt', textAlign: 'center', fontWeight: 600 }}>{value}</td>
            <td style={{ padding: '3pt 5pt', textAlign: 'center', fontWeight: 600,
              color: cls?.color || '#736b64' }}>{emo(cls?.label || '-')}</td>
            <td style={{ padding: '3pt 5pt', textAlign: 'center', color: '#736b64', fontSize: '7pt' }}>{ref}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ObjectivesSection – próximos passos baseados nos valores
// ─────────────────────────────────────────────────────────────────────────────

function ObjectivesSection({ record, gender }) {
  if (!record) return null;
  const steps = [];

  const gc  = parseFloat(record.gordura_corporal);
  const gv  = parseFloat(record.gordura_visceral);
  const ms  = parseFloat(record.musculos_esqueleticos);
  const imc = parseFloat(record.imc);

  const gcHigh = gender === 'M' ? gc >= 20 : gc >= 26;
  const msLow  = gender === 'M' ? ms < 33  : ms < 24;

  if (gcHigh)  steps.push('Redução de gordura corporal com estratégia alimentar hipocalórica e exercício aeróbio regular.');
  if (gv >= 10) steps.push('Controle da gordura visceral com foco em alimentos anti-inflamatórios e redução de açúcares simples.');
  if (msLow)   steps.push('Aumento de massa muscular esquelética com adequado aporte proteico e treino resistido.');
  if (imc >= 25 && imc < 30) steps.push('Meta de peso: alcançar IMC saudável (18,5–24,9) com déficit calórico gradual e sustentável.');
  if (imc >= 30) steps.push('Acompanhamento contínuo para redução progressiva do IMC com metas mensais realistas.');
  if (steps.length === 0) steps.push('Manutenção dos indicadores atuais com alimentação equilibrada e estilo de vida ativo.');

  return (
    <div>
      <h2 style={{ fontFamily: SERIF, fontSize: '10pt', fontWeight: 600, color: '#2f2a26',
        margin: '0 0 6pt', borderBottom: '1pt solid #d9d0c6', paddingBottom: '4pt' }}>
        Objetivos e Próximos Passos
      </h2>
      <ul style={{ margin: 0, paddingLeft: '13pt', fontSize: '8.5pt', lineHeight: 1.7, color: '#4a4440', fontFamily: FONT }}>
        {steps.map((s, i) => <li key={i}>{s}</li>)}
        <li>Retornar para reavaliação corporal em aproximadamente 3 meses.</li>
      </ul>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ReturnDateBox – caixa de data de retorno
// ─────────────────────────────────────────────────────────────────────────────

function ReturnDateBox({ proximoRetorno }) {
  if (!proximoRetorno) return null;
  return (
    <div style={{
      border: `1.5pt solid ${BRAND}`,
      borderRadius: '7pt',
      padding: '8pt 14pt',
      display: 'flex',
      alignItems: 'center',
      gap: '10pt',
      background: '#fbf8f5',
    }}>
      <div style={{ fontSize: '18pt', lineHeight: 1 }}><Ico e="📅" /></div>
      <div>
        <p style={{ margin: '0 0 1pt', fontSize: '7.5pt', color: '#736b64', textTransform: 'uppercase',
          letterSpacing: '0.06em', fontFamily: FONT }}>Próximo Retorno Sugerido</p>
        <p style={{ margin: 0, fontSize: '11pt', fontWeight: 600, color: '#2f2a26', fontFamily: SERIF }}>
          {proximoRetorno}
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PrintEvolution  –  componente principal via React Portal
// ─────────────────────────────────────────────────────────────────────────────

export default function PrintEvolution({ patient, records, evolution, motivation, nutricionista, proximoRetorno }) {
  const marca = useMarca();
  const LOGO_URL = marca.logo || LOGO_LEVNUTRI;
  if (!patient) return null;

  const isFirstConsult = records.length === 1;
  const first  = records[records.length - 1];
  const last   = records[0];
  const gender = patient.gender || 'F';

  // Idade real a partir de data_nascimento
  const idadeReal = (() => {
    if (!patient.data_nascimento) return null;
    const hoje = new Date();
    const nasc = new Date(patient.data_nascimento + 'T12:00:00');
    let age = hoje.getFullYear() - nasc.getFullYear();
    const m = hoje.getMonth() - nasc.getMonth();
    if (m < 0 || (m === 0 && hoje.getDate() < nasc.getDate())) age--;
    return age > 0 ? age : null;
  })();

  const chartData = [...(evolution || [])].reverse().map(r => ({
    ...r,
    data_label: new Date(r.data_medicao + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
  }));

  const fmtDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('pt-BR') : '-';
  const fmtVal  = (v, u = '') => (v != null && v !== '') ? `${v}${u}` : '-';

  const goodDown = (k) => ['peso','gordura_corporal','gordura_visceral','imc','idade_corporal'].includes(k);
  const diffVal  = (key) => {
    if (!first || !last || first.id === last.id) return null;
    const d = parseFloat(last[key]) - parseFloat(first[key]);
    return isNaN(d) ? null : d;
  };
  const diffColor = (key) => {
    const d = diffVal(key);
    if (d === null) return '#736b64';
    return goodDown(key) ? (d < 0 ? '#46714f' : d > 0 ? '#a8483f' : '#736b64')
                         : (d > 0 ? '#46714f' : d < 0 ? '#a8483f' : '#736b64');
  };

  const indicators = [
    { key: 'peso',                  label: 'Peso',              unit: ' kg'   },
    { key: 'gordura_corporal',      label: 'Gordura Corporal',  unit: '%'     },
    { key: 'imc',                   label: 'IMC',               unit: ''      },
    { key: 'musculos_esqueleticos', label: 'Músculos Esq.',     unit: '%'     },
    { key: 'gordura_visceral',      label: 'Gordura Visceral',  unit: ' nív.' },
    { key: 'metabolismo_basal',     label: 'Metabolismo Basal', unit: ' kcal' },
    { key: 'idade_corporal',        label: 'Idade Corporal',    unit: ' anos' },
  ];

  const charts = [
    { dataKey: 'peso',                  color: '#D4A574', unit: ' kg',   title: 'Peso (kg)' },
    { dataKey: 'gordura_corporal',      color: '#e8c9a8', unit: '%',     title: 'Gordura Corporal (%)' },
    { dataKey: 'musculos_esqueleticos', color: '#7189a6', unit: '%',     title: 'Músculos Esq. (%)' },
    { dataKey: 'imc',                   color: '#b9d2bd', unit: '',      title: 'IMC' },
    { dataKey: 'gordura_visceral',      color: '#e3b3ad', unit: ' nív.', title: 'Gordura Visceral' },
    { dataKey: 'metabolismo_basal',     color: '#b8c7d8', unit: ' kcal', title: 'Metabolismo Basal' },
    { dataKey: 'idade_corporal',        color: '#b8c7d8', unit: ' anos', title: 'Idade Corporal' },
  ];

  // ── HEADER compartilhado ────────────────────────────────────────────────────
  const Header = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      paddingBottom: '9pt', borderBottom: `2pt solid ${BRAND}`, marginBottom: '12pt' }}>
      <img src={LOGO_URL} alt={marca.businessName || 'LevNutri'} style={{ ...reportLogoStyle(marca, 34), objectFit: 'contain' }}/>
      <div style={{ textAlign: 'right' }}>
        {nutricionista?.name_display && (
          <p style={{ margin: 0, fontSize: '9pt', fontWeight: 600, fontFamily: FONT }}>{nutricionista.name_display}</p>
        )}
        {nutricionista?.crn && (
          <p style={{ margin: 0, fontSize: '8pt', color: '#736b64', fontFamily: FONT }}>{nutricionista.crn}</p>
        )}
        <p style={{ margin: 0, fontSize: '8pt', color: '#736b64', fontFamily: FONT }}>
          Emitido em {new Date().toLocaleDateString('pt-BR')}
        </p>
      </div>
    </div>
  );

  // ── RODAPÉ compartilhado ────────────────────────────────────────────────────
  const Footer = () => (
    <div style={{ borderTop: '0.8pt solid #d9d0c6', paddingTop: '7pt',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <p style={{ margin: 0, fontSize: '6.5pt', color: '#d9d0c6', fontFamily: FONT }}>
        {marca.businessName ? `${marca.businessName} • ` : ''}Gerado com LevNutri
      </p>
      <p style={{ margin: 0, fontSize: '6.5pt', color: '#d9d0c6', fontFamily: FONT }}>
        Este relatório foi gerado eletronicamente e não substitui a avaliação clínica.
      </p>
    </div>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // MODO 1  –  Avaliação Corporal Inicial (única medição)
  // ══════════════════════════════════════════════════════════════════════════
  if (isFirstConsult) {
    const content = (
      <div className="lev-print-evolution"
        style={{ fontFamily: FONT, color: '#2f2a26', background: '#ffffff', padding: 0 }}>

        <Header />

        {/* Título */}
        <h1 style={{ fontFamily: SERIF, fontSize: '17pt', fontWeight: 600, color: '#2f2a26',
          margin: '0 0 2pt', textAlign: 'center' }}>
          Avaliação Corporal Inicial
        </h1>
        <p style={{ textAlign: 'center', color: '#736b64', fontSize: '8.5pt', marginBottom: '14pt', fontFamily: FONT }}>
          {patient.name} — 1ª medição — {fmtDate(last?.data_medicao)}
        </p>

        {/* Silhueta + tabela de classificações */}
        <div className="no-break"
          style={{ display: 'flex', gap: '16pt', marginBottom: '14pt', alignItems: 'flex-start' }}>
          <div style={{ flexShrink: 0 }}>
            <h2 style={{ fontFamily: SERIF, fontSize: '10pt', fontWeight: 600, color: '#4a4440',
              margin: '0 0 6pt', textAlign: 'center' }}>
              Composição Atual
            </h2>
            <BodyWithAnnotations gender={gender} last={last} first={last} printMode />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontFamily: SERIF, fontSize: '10pt', fontWeight: 600, color: '#2f2a26',
              margin: '0 0 7pt', borderBottom: '1pt solid #d9d0c6', paddingBottom: '4pt' }}>
              Classificação — Padrão OMRON HBF-514C
            </h2>
            <ClassificationTable record={last} gender={gender} idadeReal={idadeReal} />
          </div>
        </div>

        {/* Objetivos e próximos passos */}
        <div className="no-break"
          style={{ border: '0.5pt solid #e8c9a8', borderRadius: '7pt',
            padding: '10pt 12pt', marginBottom: '12pt' }}>
          <ObjectivesSection record={last} gender={gender} />
        </div>

        {/* Mensagem motivacional */}
        {motivation && (
          <div className="no-break"
            style={{ background: 'linear-gradient(135deg,#fbf8f5,#fbf8f5)',
              border: '1pt solid #e8c9a8', borderRadius: '7pt',
              padding: '9pt 14pt', marginBottom: '12pt' }}>
            <p style={{ fontFamily: SERIF, fontSize: '10.5pt', fontStyle: 'italic',
              color: '#9a6a42', margin: 0, lineHeight: 1.65 }}>
              "{motivation}"
            </p>
          </div>
        )}

        {/* Retorno + assinatura */}
        <div className="no-break"
          style={{ display: 'flex', gap: '14pt', marginBottom: '14pt', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <ReturnDateBox proximoRetorno={proximoRetorno} />
          </div>
          <div style={{ flex: 1, paddingTop: '4pt' }}>
            <div style={{ borderTop: '0.8pt solid #d9d0c6', marginTop: '22pt', paddingTop: '5pt',
              textAlign: 'center', fontSize: '7.5pt', color: '#736b64', fontFamily: FONT }}>
              {nutricionista?.name_display || 'Nutricionista'}
              {nutricionista?.crn ? ` — ${nutricionista.crn}` : ''}
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );

    return ReactDOM.createPortal(content, document.body);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // MODO 2  –  Relatório de Evolução Corporal (2+ medições)
  // ══════════════════════════════════════════════════════════════════════════
  const content = (
    <div className="lev-print-evolution"
      style={{ fontFamily: FONT, color: '#2f2a26', background: '#ffffff', padding: 0 }}>

      <Header />

      {/* Título */}
      <h1 style={{ fontFamily: SERIF, fontSize: '17pt', fontWeight: 600, color: '#2f2a26',
        margin: '0 0 3pt', textAlign: 'center' }}>
        Relatório de Evolução Corporal
      </h1>
      <p style={{ textAlign: 'center', color: '#736b64', fontSize: '8.5pt', marginBottom: '14pt', fontFamily: FONT }}>
        {patient.name} — {records.length} medição{records.length !== 1 ? 'ões' : ''} registrada{records.length !== 1 ? 's' : ''}&ensp;
        ({fmtDate(first?.data_medicao)} → {fmtDate(last?.data_medicao)})
      </p>

      {/* Silhueta + tabela comparativa */}
      <div className="no-break"
        style={{ display: 'flex', gap: '16pt', marginBottom: '14pt', alignItems: 'flex-start' }}>
        <div style={{ flexShrink: 0 }}>
          <h2 style={{ fontFamily: SERIF, fontSize: '10pt', fontWeight: 600, color: '#4a4440',
            margin: '0 0 6pt', textAlign: 'center' }}>
            Composição — {fmtDate(last?.data_medicao)}
          </h2>
          <BodyWithAnnotations gender={gender} last={last} first={first} printMode />
        </div>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontFamily: SERIF, fontSize: '10pt', fontWeight: 600, color: '#2f2a26',
            margin: '0 0 7pt', borderBottom: '1pt solid #d9d0c6', paddingBottom: '4pt' }}>
            Primeira × Última Medição
          </h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '8pt', fontFamily: FONT }}>
            <thead>
              <tr style={{ background: '#fbf8f5' }}>
                {['Indicador', `1ª   ${fmtDate(first?.data_medicao)}`, `Última  ${fmtDate(last?.data_medicao)}`, 'Variação'].map(h => (
                  <th key={h} style={{ padding: '4pt 5pt', textAlign: h === 'Indicador' ? 'left' : 'center',
                    color: '#4a4440', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {indicators.map(({ key, label, unit }) => {
                const d = diffVal(key);
                return (
                  <tr key={key} style={{ borderBottom: '0.5pt solid #ebe3da' }}>
                    <td style={{ padding: '3pt 5pt', fontWeight: 500 }}>{emo(label)}</td>
                    <td style={{ padding: '3pt 5pt', textAlign: 'center' }}>{fmtVal(first?.[key], unit)}</td>
                    <td style={{ padding: '3pt 5pt', textAlign: 'center' }}>{fmtVal(last?.[key], unit)}</td>
                    <td style={{ padding: '3pt 5pt', textAlign: 'center', fontWeight: 600, color: diffColor(key) }}>
                      {d !== null ? `${d > 0 ? '+' : ''}${d.toFixed(1)}${unit}` : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Gráficos SVG */}
      {chartData.length >= 2 && (
        <div className="no-break" style={{ marginBottom: '14pt' }}>
          <h2 style={{ fontFamily: SERIF, fontSize: '10pt', fontWeight: 600, color: '#2f2a26',
            margin: '0 0 8pt', borderBottom: '1pt solid #d9d0c6', paddingBottom: '4pt' }}>
            Evolução ao Longo do Tempo
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '10pt', rowGap: '6pt' }}>
            {charts.map(c => (
              <div key={c.dataKey} style={{ border: '0.5pt solid #ebe3da', borderRadius: '5pt', padding: '4pt 6pt' }}>
                <PrintLineChart
                  data={chartData} dataKey={c.dataKey}
                  color={c.color} unit={c.unit} title={c.title}
                  width={245} height={115}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mensagem motivacional */}
      {motivation && (
        <div className="no-break"
          style={{ background: 'linear-gradient(135deg,#fbf8f5,#fbf8f5)',
            border: '1pt solid #e8c9a8', borderRadius: '7pt',
            padding: '10pt 14pt', marginBottom: '12pt' }}>
          <p style={{ fontFamily: SERIF, fontSize: '10.5pt', fontStyle: 'italic',
            color: '#9a6a42', margin: 0, lineHeight: 1.65 }}>
            "{motivation}"
          </p>
        </div>
      )}

      {/* Próximo retorno */}
      {proximoRetorno && (
        <div className="no-break" style={{ marginBottom: '14pt' }}>
          <ReturnDateBox proximoRetorno={proximoRetorno} />
        </div>
      )}

      <Footer />
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
}
